import { MovementPhase } from './movementData'
// Munitions status types based on ammunition percentage
export type MunitionsStatus = 'green' | 'amber' | 'red' | 'black'
// Interface for ammunition nodes
export interface AmmunitionNode {
  id: string
  name: string
  label: string
  coordinates: [number, number] // [latitude, longitude]
  description: string
  isAlternate: boolean // Whether this is an alternate node
}
// Interface for ammunition distribution corridors
export interface AmmunitionDistributionCorridor {
  id: string
  name: string
  coordinates: [number, number][] // Array of coordinate pairs forming a route
  description: string
  nodeId: string // ID of the ammunition node this corridor is connected to
}
// Interface for daily ammunition status
export interface DailyAmmunitionStatus {
  day: number
  percentageOnHand: number
  status: MunitionsStatus
  notes: string
}
// Status colors for visual representation
export const munitionsStatusColors: Record<MunitionsStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
  black: '#1F2937', // Black
}
// Get munitions status based on percentage
export const getMunitionsStatus = (percentage: number): MunitionsStatus => {
  if (percentage >= 75) return 'green'
  if (percentage >= 50) return 'amber'
  if (percentage >= 25) return 'red'
  return 'black'
}
// Ammunition nodes data (using the same locations as fuel nodes)
export const ammunitionNodes: AmmunitionNode[] = [
  {
    id: 'fort-stockton',
    name: 'F1 - Fort Stockton Class V Supply Point',
    label: 'F1',
    coordinates: [30.8941, -102.8793], // Fort Stockton
    description: 'Class V munitions stored here. Supports full reload on D+5.',
    isAlternate: false,
  },
  {
    id: 'alpine',
    name: 'F1 (Alt) - Alpine Class V Supply Point',
    label: 'F1-ALT',
    coordinates: [30.3584, -103.6614], // Alpine
    description:
      'Alternate route node. Class V support resupplied on same cycle.',
    isAlternate: true,
  },
]
// Ammunition distribution corridors
export const ammunitionDistributionCorridors: AmmunitionDistributionCorridor[] =
  [
    {
      id: 'ad1-stockton',
      name: 'AD1 - Fort Stockton to Del Rio',
      coordinates: [
        [30.8941, -102.8793], // Fort Stockton
        [30.1422, -102.3979], // Sanderson (intermediate point)
        [29.3627, -100.8968], // Del Rio
      ],
      description: 'Primary ammunition distribution corridor',
      nodeId: 'fort-stockton',
    },
    {
      id: 'ad1-alpine',
      name: 'AD1 - Alpine to Del Rio',
      coordinates: [
        [30.3584, -103.6614], // Alpine
        [30.1422, -102.3979], // Sanderson (intermediate point)
        [29.3627, -100.8968], // Del Rio
      ],
      description: 'Alternate ammunition distribution corridor',
      nodeId: 'alpine',
    },
  ]
// Daily ammunition status data
export const dailyAmmunitionStatus: DailyAmmunitionStatus[] = [
  {
    day: 1,
    percentageOnHand: 85,
    status: 'green',
    notes: 'Initial full load',
  },
  {
    day: 2,
    percentageOnHand: 66,
    status: 'amber',
    notes: 'Warning threshold approaching',
  },
  {
    day: 3,
    percentageOnHand: 47,
    status: 'amber',
    notes: 'Moderate drawdown',
  },
  {
    day: 4,
    percentageOnHand: 27,
    status: 'red',
    notes: 'Low – prepare to resupply',
  },
  {
    day: 5,
    percentageOnHand: 100,
    status: 'green',
    notes: 'Resupply event at Node F1',
  },
  {
    day: 6,
    percentageOnHand: 81,
    status: 'green',
    notes: 'Post-resupply draw',
  },
  {
    day: 7,
    percentageOnHand: 62,
    status: 'amber',
    notes: 'End of mission phase, ammo reduced',
  },
]
// Helper function to get ammunition status for a specific day
export const getAmmunitionStatusForDay = (
  day: number,
): DailyAmmunitionStatus => {
  return (
    dailyAmmunitionStatus.find((status) => status.day === day) ||
    dailyAmmunitionStatus[0]
  )
}
// Helper function to convert time point to day number (reusing from fuel data)
export const timePointToDay = (timePoint: string): number => {
  switch (timePoint) {
    case '24h':
      return 1
    case '48h':
      return 2
    case '72h':
      return 3
    case '96h':
      return 4
    case '5d':
      return 5
    case '6d':
      return 6
    case '7d':
      return 7
    default:
      return 0 // For "now", show day 0 (not started)
  }
}
